// @flow
import moment, { type Moment } from "moment";

// These numbers are estimated for all postcodes
export const DAYS_ENGAGED_TO_LISTED = 7;
export const DAYS_EXCHANGED_TO_COMPLETION = 14;

export type TimelineData = {
  dates: {
    engagedAgent: string,
    listed: string,
    offerAccepted: string,
    exchange: string,
    completion: string,
  },
  duration: {
    engagedAgent: number,
    listed: number,
    offerAccepted: number,
    exchange: number,
  },
};

export const formatDate = (date: Moment) => date.format("Do MMMM YYYY");

export const getTimelineInfo = (
  moveDate: Moment,
  daysListingToOffer: number,
  daysListingToExchange: number,
): TimelineData => {
  const duration = {
    engagedAgent: DAYS_ENGAGED_TO_LISTED,
    listed: daysListingToOffer,
    offerAccepted: daysListingToExchange - daysListingToOffer,
    exchange: DAYS_EXCHANGED_TO_COMPLETION,
  };
  const completion = moveDate.clone();
  const exchange = completion.clone().subtract(duration.exchange, "days");
  const offerAccepted = exchange
    .clone()
    .subtract(duration.offerAccepted, "days");
  const listed = offerAccepted.clone().subtract(duration.listed, "days");
  const engagedAgent = listed.clone().subtract(duration.engagedAgent, "days");

  const dates = {
    completion: formatDate(completion),
    exchange: formatDate(exchange),
    offerAccepted: formatDate(offerAccepted),
    listed: formatDate(listed),
    engagedAgent: formatDate(engagedAgent),
  };
  return { dates, duration };
};

export const createTimeline = (
  exampleView: boolean,
  listDateInPast: boolean,
  moveDate: Moment,
  averageTimeToExchange: ?number,
  averageTimeToSold: ?number,
  today: Moment,
): TimelineData => {
  if ((!averageTimeToExchange || !averageTimeToSold) && exampleView) {
    return getExampleTimeline(today);
  }
  if (
    !exampleView &&
    !listDateInPast &&
    averageTimeToSold &&
    averageTimeToExchange
  ) {
    return getTimelineInfo(moveDate, averageTimeToSold, averageTimeToExchange);
  }
  if (
    !exampleView &&
    listDateInPast &&
    averageTimeToSold &&
    averageTimeToExchange
  ) {
    const completionDate = createCompletionDateFromToday(
      averageTimeToExchange,
      today,
    );
    return getTimelineInfo(
      completionDate,
      averageTimeToSold,
      averageTimeToExchange,
    );
  }
  return getExampleTimeline(today);
};

export const daysToWeeks = (days: number): number => Math.round(days / 7);

// Creates example data based on a moveDate 225 days in the future and some example times (taken from east london postcode)
export const getExampleTimeline = (today: Moment) => {
  const daysIntoFuture = 225;
  const exampleDaysListingToOffer = 118;
  const exampleDaysListingToExchange = 207;

  const moveDate = today.clone().add(daysIntoFuture, "days");
  return getTimelineInfo(
    moveDate,
    exampleDaysListingToOffer,
    exampleDaysListingToExchange,
  );
};

export const getAverageCompletionWeeks = (
  averageTimeToExchange: ?number,
): number => {
  if (averageTimeToExchange)
    return daysToWeeks(averageTimeToExchange + DAYS_EXCHANGED_TO_COMPLETION);
  const exampleWeeksListingToCompletion = 32;
  return exampleWeeksListingToCompletion;
};

export const isInThePast = (testDate: Moment, today: Moment): boolean =>
  moment(testDate).isBefore(today);

export const isListDateInPast = (
  moveDate: Moment,
  averageTimeToExchange: number,
  today: Moment,
) => {
  const totalSaleLength =
    averageTimeToExchange +
    DAYS_ENGAGED_TO_LISTED +
    DAYS_EXCHANGED_TO_COMPLETION;
  const listDate = moveDate.clone().subtract(totalSaleLength, "days");
  return {
    listDate: listDate.clone().format("YYYY-MM-DD"),
    listDateInPast: listDate.isBefore(today),
  };
};

export const createCompletionDateFromToday = (
  averageTimeToExchange: number,
  today: Moment,
) => {
  const totalSaleLength =
    averageTimeToExchange +
    DAYS_ENGAGED_TO_LISTED +
    DAYS_EXCHANGED_TO_COMPLETION;
  return today.clone().add(totalSaleLength, "days");
};

export const getDaysAhead = (moveDate: Moment, today: Moment) =>
  moveDate.diff(today, "days", true);
